/**
 * A pillar's heading, and what is said when it is not there.
 *
 * **Since slice 65 the Overview mounts this for one case only**: a workspace
 * whose plan has no Connect, in place of the dashboard, because the dashboard
 * is Connect's content (every figure on it is a message Wend handed to SES or
 * Meta's Cloud API). Before slice 65 that pillar was called Reach; Reach is
 * now paid ads, and its place on this page is a card in `PillarStrip`, not a
 * section here. The strip sits above this component too, so a workspace with
 * Reach or Sense but no Connect still sees where those start — and the strip
 * draws no card for a pillar that is not on the plan, so "Not on this plan"
 * is said once, here, under this heading, rather than once on a strip card and
 * again directly below it.
 *
 * **An unbuilt pillar is stated in words and never mocked with empty tiles.**
 * The precedent is slice 4’s WhatsApp card, which said "nothing here has been
 * built" in as many words and read as honest rather than as unfinished. A row
 * of greyed-out charts reads as a product that is broken today rather than one
 * that has not been written yet, and it invites somebody to ask why the numbers
 * are all zero. Slice 26 then took Flow off this page altogether; its words
 * below are for a mount that does not exist and must stay true if one does.
 *
 * **`not_built` beats `not_entitled`**, decided in `overviewPillar` rather than
 * here so that the sidebar group and this card cannot disagree: being sold
 * something that does not exist is worse than being told it is not on your
 * plan, and "not built yet" stays true after they buy it.
 *
 * ## THE SENSE PARAGRAPHS THAT USED TO BE HERE
 *
 * This header argued at length for a Sense note written per workspace — the
 * tracking-off sentence, the hosted-pictures variant, where WhatsApp read
 * receipts live. Slice 26 took the Sense card off the Overview and the note
 * with it (the comment above `PillarSection` records where each guarantee went),
 * and slice 65 removed the last of it: Sense's "nothing here has been built"
 * paragraph, which had been false since slice 59 shipped Sense's first screen.
 * `note` stays as a prop because it costs nothing and an unbuilt pillar may
 * again have something true to add about one workspace; nothing passes it now.
 *
 * ## WHAT SLICE 20 CHANGED
 *
 * Nothing but where the file lives, and one thing about the frame: the pillar’s
 * children (Reach’s then, Connect’s since slice 65) are a dashboard rather than
 * a stack of cards, so this
 * component’s heading and badge are the only chrome it contributes. It renders
 * `children` for `available` and a card of words otherwise, which is the whole
 * of its job and was already the whole of its job.
 */
import {
  type Feature,
  type PillarState,
} from '@wend/shared';
import { Badge, Card, CardContent } from '@wend/ui';
import { type ReactNode } from 'react';

import { PILLAR_IDENTITY, PILLAR_LABELS } from '../../lib/pillars';

interface PillarCopy {
  readonly name: string;
  readonly tagline: string;
  /** Said when the software does not exist. Stated, never mocked. */
  readonly notBuilt: string;
  /** Said when it exists but this plan does not include it. */
  readonly notEntitled: string;
}

/**
 * What each pillar is, and what to say when it is not there.
 *
 * **Since slice 65 only one of these is ever drawn, and it is Connect's.**
 * `HomePage` mounts this component instead of the dashboard for a workspace
 * whose plan has no Connect — the dashboard is Connect's content, so that is
 * the one pillar state the Overview still has to explain. The other entries
 * stay because the map is total over `Feature` (a pillar added to the enum
 * stops this compiling until somebody writes its words), and each is kept
 * true rather than left as filler that a later mount would print:
 *
 *   - **The taglines of the built pillars are their purpose lines** from
 *     `lib/pillars.tsx`, the words the Overview's strip and the page headers
 *     use. The first cut gave Connect the old Reach tagline, "Getting a
 *     message to somebody: lists, segments, templates, campaigns, delivery" —
 *     a second description of Connect a few pixels under the strip's, and one
 *     that filed the shared layers (lists, segments, templates) under a pillar
 *     this plan does not have, while the sidebar offered them as live links
 *     under "Audiences & templates".
 *   - **Connect** kept the old Reach "not on this plan" sentence. Before slice
 *     65 "Reach" meant the email and WhatsApp half; that half is Connect now,
 *     and what is missing without it did not change.
 *   - **Reach** is paid ads on the workspace's own Meta ad account, and its
 *     sentence says what is missing in those terms — no ads drafted or run.
 *   - **Sense** is built (slices 58-64). Its old `notBuilt` paragraph said
 *     "nothing here has been built" and listed two pieces that lived "under
 *     Reach rather than here"; both claims were false by slice 59 and the
 *     second named a pillar that no longer means messaging. `overviewPillar`
 *     can never return `not_built` for a built pillar, so the field is empty,
 *     as Reach's always was.
 *   - **Flow** is built since slice 69 (engine 68, builder 69). Its old
 *     `notBuilt` paragraph said no automation engine existed behind this
 *     heading — false from slice 68 — so the field is empty like the other
 *     built pillars', its tagline is `PILLAR_IDENTITY.flow.purpose`, and its
 *     not-on-this-plan sentence no longer adds "not built yet either".
 */
const PILLAR_COPY: Readonly<Record<Feature, PillarCopy>> = {
  reach: {
    name: PILLAR_LABELS.reach,
    tagline: PILLAR_IDENTITY.reach.purpose,
    notBuilt: '',
    notEntitled:
      'This plan does not include Reach, so no ads are drafted or run from Wend. Talk to us if that is wrong.',
  },
  connect: {
    name: PILLAR_LABELS.connect,
    tagline: PILLAR_IDENTITY.connect.purpose,
    notBuilt: '',
    notEntitled:
      'This plan does not include Connect, so there is nothing to send with and no delivery to report on. Talk to us if that is wrong.',
  },
  flow: {
    name: PILLAR_LABELS.flow,
    tagline: PILLAR_IDENTITY.flow.purpose,
    notBuilt: '',
    notEntitled: 'This plan does not include Flow, so no journeys run from Wend. Talk to us if that is wrong.',
  },
  sense: {
    name: PILLAR_LABELS.sense,
    tagline: PILLAR_IDENTITY.sense.purpose,
    notBuilt: '',
    notEntitled: 'This plan does not include Sense.',
  },
};

/*
 * `senseTrackingSentence` WAS HERE, AND WENT WITH THE SENSE CARD — SLICE 26.
 *
 * It wrote the note under the Sense pillar: a paragraph saying this workspace
 * has tracking off, that Wend adds no pixel and no rewritten links, and why
 * there is therefore no open rate rather than a zero. Every word of it was
 * true and none of it is lost.
 *
 * **It was the second copy.** `EngagementCard` renders
 * `trackingOffDisclosure(imageDelivery)` from the same wire field, saying the
 * same thing to the same reader on the same page — the duplication slice 25
 * recorded as Overview finding 7, *"printed twice, differing only in person"*.
 * With the Sense card off the screen this copy had no mount point left, and
 * keeping an exported function that nothing renders is the same mistake as the
 * unmounted chart component slice 25 deleted, with a paragraph attached.
 *
 * The guarantee it carried is not lost either. It took `imageDelivery` as a
 * required argument so the mode could only come from the same response as
 * `tracking`, never from a default that guesses at the deployment.
 * `EngagementCard` reads both off one response by construction, and
 * `verify-dashboard` renders it on a hosting deployment to prove the hosted
 * sentence rather than the embedded promise — which is a stronger instrument
 * than the source probe that used to watch this call site.
 */

export function PillarSection({
  feature,
  state,
  note = null,
  children,
}: {
  feature: Feature;
  state: PillarState;
  /**
   * A second paragraph for the unbuilt card, when there is something true about
   * *this* workspace to add to a sentence about the product. Null prints
   * nothing, and that is the default rather than a fallback string because a
   * default would be a claim about this workspace that nobody has checked.
   */
  note?: string | null;
  children?: ReactNode;
}) {
  const copy = PILLAR_COPY[feature];
  const headingId = `pillar-${feature}`;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h2
            id={headingId}
            className="text-[1.35rem] font-semibold tracking-[-0.025em] text-text"
          >
            {copy.name}
          </h2>
          {state === 'not_built' && <Badge tone="neutral">Not built yet</Badge>}
          {state === 'not_entitled' && <Badge tone="neutral">Not on this plan</Badge>}
        </div>
        <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">{copy.tagline}</p>
      </div>

      {state === 'available' ? (
        children
      ) : (
        <Card>
          <CardContent className="flex flex-col gap-3 px-6 py-6">
            <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">
              {state === 'not_built' ? copy.notBuilt : copy.notEntitled}
            </p>
            {note !== null && (
              <p className="max-w-prose text-[0.9rem] leading-relaxed text-muted">{note}</p>
            )}
          </CardContent>
        </Card>
      )}
    </section>
  );
}
